import React, { useState } from 'react';
import { Search, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import JournalCard from '../components/JournalCard';
import AdminEditModal from '../components/AdminEditModal';
import ConfirmModal from '../components/ConfirmModal';

const FIELDS = [
  'All Fields',
  'Computer Science & Quantum Physics',
  'Materials Science & Engineering',
  'Biomedical Sciences & Molecular Biology',
  'Computer Science & Artificial Intelligence',
];

const JournalsPage = () => {
  const { user } = useAuth();
  const { journals, editJournal, deleteJournal } = useData();
  const [search, setSearch] = useState('');
  const [field, setField] = useState('All Fields');
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isAdmin = user && user.role === 'superadmin'; // Only superadmin can edit/delete journals

  const filtered = journals.filter(j => {
    const matchSearch =
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      j.description.toLowerCase().includes(search.toLowerCase());
    const matchField = field === 'All Fields' || j.field === field;
    return matchSearch && matchField;
  });

  const handleSaveEdit = (updatedData) => {
    editJournal(editItem.id, updatedData);
    setEditItem(null);
  };

  const handleConfirmDelete = () => {
    deleteJournal(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <div className="bg-white" data-testid="journals-page">
      {/* Page header */}
      <div className="bg-[#F5F7FA] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="section-overline mb-2">Publications</p>
          <h1 className="font-serif text-4xl font-semibold text-[#1F1F1F] mb-3">Journals</h1>
          <p className="text-[#6B7280] max-w-2xl text-sm leading-relaxed">
            Browse our portfolio of peer-reviewed journals spanning quantum science, materials, biomedicine, and artificial intelligence.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Filters row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search journals by title or keyword..."
              className="w-full pl-9 pr-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] transition-colors"
              data-testid="journals-search-input"
            />
          </div>
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
            <select
              value={field}
              onChange={e => setField(e.target.value)}
              className="pl-8 pr-8 py-2.5 border border-[#E5E7EB] text-sm text-[#374151] bg-white focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] appearance-none cursor-pointer"
              data-testid="journals-field-filter"
            >
              {FIELDS.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
        </div>

        {/* Results meta row */}
        <div className="mb-5 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-[#6B7280]">
            Showing <span className="font-semibold text-[#1F1F1F]">{filtered.length}</span> journal{filtered.length !== 1 ? 's' : ''}
          </p>
          {isAdmin && (
            <span
              className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1"
              data-testid="admin-mode-label"
            >
              Admin Mode — Edit &amp; Delete enabled
            </span>
          )}
        </div>

        {/* Cards */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 border border-[#E5E7EB]">
            <p className="text-[#6B7280] text-sm">No journals found matching your search criteria.</p>
            <button
              onClick={() => { setSearch(''); setField('All Fields'); }}
              className="mt-3 text-sm text-[#1A73E8] hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map(journal => (
              <JournalCard
                key={journal.id}
                journal={journal}
                isAdmin={isAdmin}
                onEdit={setEditItem}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </div>

      {/* Admin Modals */}
      <AdminEditModal
        isOpen={!!editItem}
        type="journal"
        item={editItem}
        onSave={handleSaveEdit}
        onClose={() => setEditItem(null)}
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        message={`Are you sure you want to remove "${deleteTarget?.title}" from the journals list? This will take effect for the current session.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default JournalsPage;
